import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface MobileImageViewerProps {
    src: string;
    onClose: () => void;
}

export const MobileImageViewer: React.FC<MobileImageViewerProps> = ({ src, onClose }) => {
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const lastPinchDist = useRef<number | null>(null);
    const lastOffset = useRef({ x: 0, y: 0 });
    const lastTouch = useRef<{ x: number; y: number } | null>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const getPinchDist = (touches: React.TouchList) =>
        Math.hypot(
            touches[1].clientX - touches[0].clientX,
            touches[1].clientY - touches[0].clientY
        );

    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            lastPinchDist.current = getPinchDist(e.touches);
        } else if (e.touches.length === 1) {
            lastTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            lastOffset.current = offset;
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        e.stopPropagation();
        if (e.touches.length === 2 && lastPinchDist.current !== null) {
            const dist = getPinchDist(e.touches);
            const ratio = dist / lastPinchDist.current;
            setScale(s => Math.min(Math.max(s * ratio, 0.5), 8));
            lastPinchDist.current = dist;
        } else if (e.touches.length === 1 && lastTouch.current && scale > 1) {
            const dx = e.touches[0].clientX - lastTouch.current.x;
            const dy = e.touches[0].clientY - lastTouch.current.y;
            setOffset({ x: lastOffset.current.x + dx, y: lastOffset.current.y + dy });
            lastOffset.current = { x: lastOffset.current.x + dx, y: lastOffset.current.y + dy };
            lastTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (e.touches.length < 2) lastPinchDist.current = null;
        if (e.touches.length === 0) {
            lastTouch.current = null;
            if (scale < 1.05) { setScale(1); setOffset({ x: 0, y: 0 }); }
        }
    };

    const handleDoubleTap = (() => {
        let lastTap = 0;
        return () => {
            const now = Date.now();
            if (now - lastTap < 300) {
                setScale(s => s > 1.5 ? 1 : 2.5);
                setOffset({ x: 0, y: 0 });
            }
            lastTap = now;
        };
    })();

    return (
        <div
            className="fixed inset-0 z-[500] bg-black flex items-center justify-center overflow-hidden"
            onClick={scale <= 1 ? onClose : undefined}
        >
            <img
                ref={imgRef}
                src={src}
                alt=""
                className="max-w-full max-h-full object-contain select-none"
                style={{
                    transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
                    transformOrigin: 'center',
                    touchAction: 'none',
                    transition: lastPinchDist.current ? 'none' : 'transform 0.1s ease-out',
                    cursor: scale > 1 ? 'grab' : 'default',
                }}
                draggable={false}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={(e) => { e.stopPropagation(); handleDoubleTap(); }}
            />
            <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
            >
                <X size={20} />
            </button>
            {scale > 1 && (
                <button
                    onClick={() => { setScale(1); setOffset({ x: 0, y: 0 }); }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/50 text-white text-sm"
                >
                    Reset zoom
                </button>
            )}
        </div>
    );
};
